import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import classNames from "classnames";
import styles from "./StickerPrintModal.module.scss";
import Button from "../../button/Button";
import { toast } from "react-toastify";
import { CloseIcon } from "../../../assets";
import config from "../../../config";
import stickerPrintService from "../../../apis/services/admin/stickerPrintService";
import taxPayersService from "../../../apis/services/admin/taxPayersService";
import { isValidEmail } from "../../../util/validations";
import {
  cleanSerial,
  macToProvCode,
  SERIAL_REGEX,
  PROV_CODE_REGEX,
} from "../../../util/stickers";

const StickerPrintModal = ({
  modalOpen,
  mode, // "print" | "email"
  controllers,
  companyId,
  onClose,
  onSuccess,
}) => {
  const [rows, setRows] = useState([]);
  const [copies, setCopies] = useState("1");
  const [copiesError, setCopiesError] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientError, setRecipientError] = useState(null);
  const [companyEmails, setCompanyEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [failedInfo, setFailedInfo] = useState(null);

  const isPrint = mode === "print";

  // Snapshot redova pri otvaranju — refetch u pozadini ne sme da pregazi unos
  useEffect(() => {
    if (!modalOpen) return;
    setRows(
      (controllers || []).map((controller) => ({
        id: controller.id,
        provCode: macToProvCode(controller.macAddress),
        serial: cleanSerial(controller.serialNumber),
        name: controller.name || "",
        errors: {},
      }))
    );
    setCopies("1");
    setCopiesError(null);
    setRecipientEmail("");
    setRecipientError(null);
    setCompanyEmails([]);
    setFailedInfo(null);
    if (mode === "email" && companyId) {
      taxPayersService
        .fetchCompanyInfo(companyId)
        .then((response) => {
          const users = response?.data?.company?.users || [];
          const emails = [
            ...new Set(users.map((user) => user.email).filter(Boolean)),
          ];
          setCompanyEmails(emails);
          if (emails.length > 0) setRecipientEmail(emails[0]);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, [field]: value, errors: { ...row.errors, [field]: null } }
          : row
      )
    );
  };

  const validate = () => {
    let valid = true;
    setRows((prev) =>
      prev.map((row) => {
        const errors = {};
        if (!PROV_CODE_REGEX.test(row.provCode.trim())) {
          errors.provCode = "Neispravan PROV kod";
        }
        if (!SERIAL_REGEX.test(row.serial.trim())) {
          errors.serial = "Neispravan serijski broj";
        }
        if (!row.name.trim()) {
          errors.name = "Obavezno polje";
        }
        if (Object.keys(errors).length > 0) valid = false;
        return { ...row, errors };
      })
    );
    if (isPrint) {
      const parsed = parseInt(copies, 10);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 20) {
        setCopiesError("Broj kopija mora biti između 1 i 20");
        valid = false;
      }
    } else if (!isValidEmail(recipientEmail.trim())) {
      setRecipientError("Neispravna email adresa");
      valid = false;
    }
    return valid;
  };

  const handleResult = (data) => {
    if (!data?.success) {
      setFailedInfo({ okCount: 0, messages: [data?.error || "Nepoznata greška"] });
      toast.error(data?.error || "Greška na print serveru");
      return;
    }
    const errors = data.errors || [];
    const printFailures = (data.printed || []).filter((p) => !p.printed);
    const emailResult = (data.emailed || [])[0];
    const emailFailed = !isPrint && !emailResult?.emailed;

    if (errors.length === 0 && printFailures.length === 0 && !emailFailed) {
      toast.info(
        isPrint
          ? `Odštampano ${rows.length} nalepnica` +
              (parseInt(copies, 10) > 1 ? ` (× ${copies} kopija)` : "")
          : `Nalepnice poslate na ${recipientEmail.trim()}`
      );
      onSuccess();
      return;
    }

    // Parcijalan neuspeh: uspešni redovi se sklanjaju iz forme da ponovni
    // pokušaj ne odštampa duplikate; neuspešni ostaju za ispravku
    const failedProvs = new Set(
      errors.map((message) => String(message).split(":")[0].trim().toUpperCase())
    );
    printFailures.forEach(
      (p) => p.prov_code && failedProvs.add(String(p.prov_code).toUpperCase())
    );
    const remaining = emailFailed
      ? rows
      : rows.filter((row) => failedProvs.has(row.provCode.trim().toUpperCase()));
    setRows(remaining.length > 0 ? remaining : rows);

    const messages = [
      ...errors,
      ...printFailures.map(
        (p) =>
          `${p.prov_code}: štampa nije uspela${p.error ? ` (${p.error})` : ""}`
      ),
    ];
    if (emailFailed) {
      messages.push(`Email nije poslat${emailResult?.error ? `: ${emailResult.error}` : ""}`);
    }
    setFailedInfo({ okCount: rows.length - remaining.length, messages });
    toast.warn(`Uspešno ${rows.length - remaining.length}/${rows.length}`);
  };

  const onSubmit = async () => {
    if (saving || rows.length === 0) return;
    setFailedInfo(null);
    if (!validate()) return;
    setSaving(true);
    try {
      const data = await stickerPrintService.generateStickers({
        entries: rows.map((row) => ({
          prov_code: row.provCode.trim().toUpperCase(),
          serial: row.serial.trim(),
          name: row.name.trim(),
        })),
        print: isPrint,
        email: !isPrint,
        copies: parseInt(copies, 10) || 1,
        customerEmail: recipientEmail.trim(),
      });
      handleResult(data);
    } catch (err) {
      setFailedInfo({ unreachable: true });
      toast.error("Print server nije dostupan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={modalOpen} onClose={onClose}>
      <div className={styles.modalWrap}>
        <div className={styles.modalInner}>
          <div className={styles.header}>
            <div className={styles.closeIconWrap}>
              <img
                src={CloseIcon}
                alt="close"
                onClick={onClose}
                className={styles.closeIcon}
              />
            </div>
            <p className={styles.title}>
              {isPrint
                ? `Štampaj nalepnice (${rows.length})`
                : `Pošalji nalepnice na email (${rows.length})`}
            </p>
          </div>
          <div className={styles.content}>
            <div className={styles.rowsHeader}>
              <p>PROV KOD</p>
              <p>SERIJSKI BROJ</p>
              <p>NAZIV MAŠINE</p>
            </div>
            <div className={styles.scrollArea}>
              {rows.map((row, index) => (
                <div className={styles.entryRow} key={row.id ?? index}>
                  <div className={styles.entryField}>
                    <input
                      className={classNames(
                        styles.entryInput,
                        row.errors.provCode && styles.inputError
                      )}
                      value={row.provCode}
                      onChange={(e) => updateRow(index, "provCode", e.target.value)}
                      placeholder="39D758"
                    />
                    {row.errors.provCode && (
                      <p className={styles.errorText}>{row.errors.provCode}</p>
                    )}
                  </div>
                  <div className={styles.entryField}>
                    <input
                      className={classNames(
                        styles.entryInput,
                        row.errors.serial && styles.inputError
                      )}
                      value={row.serial}
                      onChange={(e) => updateRow(index, "serial", e.target.value)}
                      placeholder="49"
                    />
                    {row.errors.serial && (
                      <p className={styles.errorText}>{row.errors.serial}</p>
                    )}
                  </div>
                  <div className={styles.entryField}>
                    <input
                      className={classNames(
                        styles.entryInput,
                        row.errors.name && styles.inputError
                      )}
                      value={row.name}
                      onChange={(e) => updateRow(index, "name", e.target.value)}
                      placeholder="Menjač 1"
                    />
                    {row.errors.name && (
                      <p className={styles.errorText}>{row.errors.name}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isPrint ? (
              <div className={styles.formGroup}>
                <label className={styles.label}>Broj kopija (po nalepnici)</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className={classNames(
                    styles.entryInput,
                    styles.copiesInput,
                    copiesError && styles.inputError
                  )}
                  value={copies}
                  onChange={(e) => {
                    setCopies(e.target.value);
                    setCopiesError(null);
                  }}
                />
                {copiesError && <p className={styles.errorText}>{copiesError}</p>}
              </div>
            ) : (
              <div className={styles.formGroup}>
                <label className={styles.label}>Email primaoca</label>
                <input
                  className={classNames(
                    styles.entryInput,
                    recipientError && styles.inputError
                  )}
                  value={recipientEmail}
                  onChange={(e) => {
                    setRecipientEmail(e.target.value);
                    setRecipientError(null);
                  }}
                  placeholder="vlasnik@perionica.rs"
                />
                {recipientError && (
                  <p className={styles.errorText}>{recipientError}</p>
                )}
                {companyEmails.length > 1 && (
                  <div className={styles.suggestions}>
                    {companyEmails.map((email) => (
                      <span
                        key={email}
                        className={styles.suggestionChip}
                        onClick={() => {
                          setRecipientEmail(email);
                          setRecipientError(null);
                        }}
                      >
                        {email}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {failedInfo && (
              <div className={styles.resultBox}>
                {failedInfo.unreachable ? (
                  <p>
                    Print server nije dostupan. Ako je ovo prvi put na ovom
                    računaru,{" "}
                    <a
                      href={config.stickerService.baseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      otvori print.simons.rs
                    </a>{" "}
                    i prihvati sertifikat, pa pokušaj ponovo. Radi samo iz
                    kancelarijske mreže.
                  </p>
                ) : (
                  <>
                    {failedInfo.okCount > 0 && (
                      <p>
                        Uspešno: {failedInfo.okCount}. Neuspešni redovi su ostali
                        u formi — ispravi ih i pokušaj ponovo.
                      </p>
                    )}
                    {failedInfo.messages.map((message, index) => (
                      <p key={index} className={styles.errorText}>
                        {message}
                      </p>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className={styles.actions}>
              <Button
                name={
                  saving
                    ? isPrint
                      ? "Štampanje..."
                      : "Slanje..."
                    : isPrint
                    ? "Štampaj"
                    : "Pošalji"
                }
                onClick={onSubmit}
                disabled={saving || rows.length === 0}
                showLoader={saving}
              />
              <Button name="Otkaži" onClick={onClose} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default StickerPrintModal;
